/**
 * waap-aiwaf-wac-tab.tsx — AI-WAF Web Access Control tab (B4 batch).
 *
 * Per-site URL path whitelists (bypass WAF detection) and auto-blacklist removal.
 * Operator picks a website from the websites list, edits its whitelist rules,
 * then saves. Removing an IP from the auto-blacklist is a separate action.
 *
 * Endpoints (anchor-scoped):
 *   GET  /waap/domains/:anchorId/website-whitelist/:siteId   → WhitelistRule[]
 *   PUT  /waap/domains/:anchorId/website-whitelist/:siteId   → save full array
 *   POST /waap/domains/:anchorId/auto-black-list             → { ip } remove one IP
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, ShieldOff } from 'lucide-react';
import {
  Card, CardHeader, CardTitle, CardBody,
  Button, IconButton, Input, Badge, Skeleton,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { EmptyState } from '@vnetwork/ui/data';
import { Field } from '@vnetwork/ui/forms';
import { TabError } from '@/pages/customers/detail/tab-states';
import { ActAsMutationConfirm } from '@/pages/operations/detail/cdn/act-as-mutation-confirm';
import { actAsClient } from '@/api/act-as-client';
import { actAsWAAPKeys } from '@/api/act-as-waap';
import { useActAsWAAPWebsites } from '@/api/act-as-waap';
import {
  useActAsWAAPWebsiteWhitelist,
  type WhitelistRule,
} from '@/api/act-as-waap-rules';

/* ── Whitelist editor ────────────────────────────────────────────────────── */

const EMPTY_ENTRY: WhitelistRule = { path: '', ruleId: '', remark: '' };

interface WhitelistPending {
  rules: WhitelistRule[];
  siteId: number;
}

function WhitelistEditor({ anchorId, siteId }: { anchorId: string; siteId: number }) {
  const { t } = useTranslation();
  const wlQ = useActAsWAAPWebsiteWhitelist(anchorId, siteId);
  const [draft, setDraft] = useState<WhitelistRule[]>([]);
  const [edited, setEdited] = useState(false);
  const [pending, setPending] = useState<WhitelistPending | null>(null);

  // Sync draft from server data on first load (only if not yet edited)
  if (!edited && !wlQ.isLoading && wlQ.data && draft.length === 0 && wlQ.data.length > 0) {
    setDraft(wlQ.data.map((r) => ({ ...r })));
  }

  function addEntry() {
    setDraft((prev) => [...prev, { ...EMPTY_ENTRY }]);
    setEdited(true);
  }

  function removeEntry(idx: number) {
    setDraft((prev) => prev.filter((_, i) => i !== idx));
    setEdited(true);
  }

  function patchEntry(idx: number, key: keyof WhitelistRule, value: string) {
    setDraft((prev) => prev.map((r, i) => i === idx ? { ...r, [key]: value } : r));
    setEdited(true);
  }

  function handleSave() {
    setPending({ rules: draft, siteId });
  }

  if (wlQ.isLoading) return <Skeleton style={{ width: '100%', height: 80 }} />;
  if (wlQ.isError) {
    return (
      <TabError
        title={t('waap_domain.wac_whitelist_load_error', 'Failed to load whitelist')}
        onRetry={() => wlQ.refetch()}
      />
    );
  }

  // Use server data as display source when not in edit-draft state
  const displayRules = edited ? draft : (wlQ.data ?? []);

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      {displayRules.length === 0 && !edited ? (
        <EmptyState
          title={t('waap_domain.wac_whitelist_empty', 'No whitelist rules')}
          description={t('waap_domain.wac_whitelist_empty_desc', 'Add URL paths to bypass WAF detection for this website.')}
        />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {displayRules.map((rule, idx) => (
            <div
              key={idx}
              style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 'var(--vn-space-2)', alignItems: 'end' }}
            >
              <Field label={idx === 0 ? t('waap_domain.wac_field_path', 'Path') : undefined}>
                {(p) => (
                  <Input
                    {...p}
                    value={rule.path}
                    onChange={(e) => patchEntry(idx, 'path', e.target.value)}
                    placeholder="/api/v1/*"
                  />
                )}
              </Field>
              <Field label={idx === 0 ? t('waap_domain.wac_field_rule_id', 'Rule ID') : undefined}>
                {(p) => (
                  <Input
                    {...p}
                    value={rule.ruleId ?? ''}
                    onChange={(e) => patchEntry(idx, 'ruleId', e.target.value)}
                    placeholder="Optional rule ID"
                  />
                )}
              </Field>
              <Field label={idx === 0 ? t('waap_domain.wac_field_remark', 'Remark') : undefined}>
                {(p) => (
                  <Input
                    {...p}
                    value={rule.remark ?? ''}
                    onChange={(e) => patchEntry(idx, 'remark', e.target.value)}
                    placeholder="Optional remark"
                  />
                )}
              </Field>
              <IconButton
                size="sm"
                variant="ghost"
                aria-label={t('common.remove', 'Remove')}
                onClick={() => removeEntry(idx)}
                style={{ marginBottom: 0 }}
              >
                <Trash2 size={13} />
              </IconButton>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
        <Button size="sm" variant="secondary" onClick={addEntry}>
          <Plus size={13} />
          {t('waap_domain.wac_add_entry', 'Add path')}
        </Button>
        {edited && (
          <Button size="sm" variant="primary" onClick={handleSave}>
            {t('waap_domain.wac_save', 'Save whitelist')}
          </Button>
        )}
      </div>

      {pending && (
        <ActAsMutationConfirm
          open
          onOpenChange={(open) => { if (!open) setPending(null); }}
          title={t('waap_domain.wac_save_title', 'Save whitelist')}
          summary={t('waap_domain.wac_save_summary', 'Save {{count}} whitelist path(s) for site {{id}}.', {
            count: pending.rules.length,
            id: pending.siteId,
          })}
          destructive={false}
          queryKeysToInvalidate={[
            actAsWAAPKeys.aspect(anchorId, `website-whitelist/${pending.siteId}`),
          ]}
          mutationFn={() =>
            actAsClient.put(
              `/waap/domains/${anchorId}/website-whitelist/${pending.siteId}`,
              pending.rules,
            )
          }
          onSuccess={() => {
            setPending(null);
            setEdited(false);
            wlQ.refetch();
          }}
        />
      )}
    </div>
  );
}

/* ── Auto-blacklist removal ──────────────────────────────────────────────── */

function AutoBlacklistRemover({ anchorId }: { anchorId: string }) {
  const { t } = useTranslation();
  const [ip, setIp] = useState('');
  const [pending, setPending] = useState(false);

  return (
    <Card>
      <CardHeader>
        <CardTitle style={{ fontSize: 'var(--vn-font-size-sm)', display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
          <ShieldOff size={14} />
          {t('waap_domain.wac_blacklist_title', 'Remove IP from Auto-Blacklist')}
        </CardTitle>
      </CardHeader>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
        <p style={{ margin: 0, fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
          {t('waap_domain.wac_blacklist_desc', 'Remove a specific IP address from the WAF auto-generated blacklist.')}
        </p>
        <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Field label={t('waap_domain.wac_blacklist_ip', 'IP Address')} style={{ flex: 1, minWidth: 200 }}>
            {(p) => (
              <Input
                {...p}
                value={ip}
                onChange={(e) => setIp(e.target.value)}
                placeholder="1.2.3.4"
              />
            )}
          </Field>
          <Button
            size="sm"
            variant="danger"
            disabled={!ip.trim()}
            onClick={() => setPending(true)}
          >
            {t('waap_domain.wac_blacklist_remove', 'Remove from blacklist')}
          </Button>
        </div>
      </CardBody>

      {pending && (
        <ActAsMutationConfirm
          open
          onOpenChange={(open) => { if (!open) setPending(false); }}
          title={t('waap_domain.wac_blacklist_confirm_title', 'Remove IP from auto-blacklist')}
          summary={t('waap_domain.wac_blacklist_confirm_summary', 'Remove {{ip}} from the WAF auto-blacklist. The IP will no longer be blocked automatically.', { ip })}
          destructive
          queryKeysToInvalidate={[]}
          mutationFn={() =>
            actAsClient.post(`/waap/domains/${anchorId}/auto-black-list`, { ip })
          }
          onSuccess={() => {
            setPending(false);
            setIp('');
          }}
        />
      )}
    </Card>
  );
}

/* ── Root component ──────────────────────────────────────────────────────── */

interface WaapAiwafWacTabProps {
  anchorId: string;
}

/**
 * AI-WAF Web Access Control (WAC) tab.
 * Site picker → whitelist editor per site + auto-blacklist IP remover.
 */
export function WaapAiwafWacTab({ anchorId }: WaapAiwafWacTabProps) {
  const { t } = useTranslation();
  const sitesQ = useActAsWAAPWebsites(anchorId);
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');

  if (sitesQ.isLoading) return <Skeleton style={{ width: '100%', height: 80 }} />;
  if (sitesQ.isError) {
    return (
      <TabError
        title={t('waap_domain.wac_sites_load_error', 'Failed to load websites')}
        onRetry={() => sitesQ.refetch()}
      />
    );
  }

  const sites = sitesQ.data ?? [];

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
      {/* Whitelist section */}
      <Card>
        <CardHeader>
          <CardTitle style={{ fontSize: 'var(--vn-font-size-sm)' }}>
            {t('waap_domain.wac_whitelist_title', 'URL Path Whitelist')}
            {sites.length > 0 && (
              <Badge style={{ marginLeft: 'var(--vn-space-2)' }} tone="neutral">
                {t('waap_domain.wac_per_site', 'per website')}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {sites.length === 0 ? (
            <Alert tone="info">
              {t('waap_domain.wac_no_sites', 'No websites found. Add a website on the Websites tab before configuring whitelists.')}
            </Alert>
          ) : (
            <>
              <Field label={t('waap_domain.wac_select_site', 'Select website')}>
                {(p) => (
                  <Select value={selectedSiteId} onValueChange={setSelectedSiteId}>
                    <SelectTrigger {...p}>
                      <SelectValue placeholder={t('waap_domain.wac_select_site_placeholder', 'Choose a website…')} />
                    </SelectTrigger>
                    <SelectContent>
                      {sites.map((site) => (
                        <SelectItem key={site.id} value={String(site.id)}>
                          {site.domain} (#{site.id})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>

              {selectedSiteId ? (
                <WhitelistEditor anchorId={anchorId} siteId={Number(selectedSiteId)} />
              ) : (
                <p style={{ margin: 0, fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
                  {t('waap_domain.wac_select_site_hint', 'Select a website above to view and edit its URL whitelist.')}
                </p>
              )}
            </>
          )}
        </CardBody>
      </Card>

      {/* Auto-blacklist removal */}
      <AutoBlacklistRemover anchorId={anchorId} />
    </div>
  );
}
